/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import * as React from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import iconForwardArrowBlack from "@assets/icon-forward-arrow-black.svg";
import iconForwardArrowWhite from "@assets/icon-forward-arrow-white.svg";
import * as stockPickerWidgetHelpers from "@components/StockPickerWidget/helpers";
import { getPercentageChangeInfo } from "@components/StockPickerWidget/helpers";
import StockPickerWidget from "@components/StockPickerWidget/StockPickerWidget";
import type { StockData } from "@components/StockPickerWidget/types";
import cx from "classnames";

import { RouteFactory } from "../routePaths";
import { Annotation, Element, Field } from "../types/Cue";

export function Headline({ text }: { text: string }) {
  return <h1>{text}</h1>;
}

export type TextProps = {
  text: string;
  annotations?: Annotation[];
  annotationContext?: Element[];
  useStockData?: (code: string) => StockData | undefined;
  className?: string;
};

export function Text({
  text,
  annotations,
  annotationContext,
  useStockData = stockPickerWidgetHelpers.useStockData,
  className,
}: TextProps): React.ReactElement {
  type Span =
    | {
        type: "TEXT";
        text: string;
      }
    | { type: "NODE"; node: React.ReactElement };

  if (annotations) {
    // Only process sequential and non-overlapping annotations
    // Overlapping annotations are NOT supported and will create rendering bugs
    // Because we have this constraint we can simplify the annotation handling code
    // by always only acting on unprocessed spans (requiring only 1-pass)
    const nodes = annotations
      ?.sort((a, b) => a.index - b.index)
      ?.reduce<Span[]>(
        (acc, an, i): Span[] => {
          const lastSpan = acc[acc.length - 1];

          if (!lastSpan || lastSpan.type === "NODE") {
            return acc;
          }

          const remainingLength = lastSpan.text.length;
          const offset = text.length - remainingLength;

          const getElement = (targetElId: string): Element | undefined => {
            return annotationContext?.find?.((ac) => ac.id === targetElId);
          };
          const getFieldFromElement = (
            el: Element | undefined,
            fieldName: string
          ): Field | undefined => {
            return el?.fields?.find((f) => f.name === fieldName);
          };

          // TODO: remove hardcoded BT styles
          // It's challenging because we want to keep Blocks generic and styleless, but we can't easily do that
          // as styles are dependent on annotations. We can't easily supply className and style props for every
          // possible annotation element. A nice API will be good.
          // Maybe we can have an API like `replacements = { ...webDefault, ...btOverrides }`,
          // Or offer an ergonomic alternative...
          const replacements: Record<
            string,
            (text: string) => React.ReactElement | null
          > = {
            "stock-picker": (text: string) => {
              const code = getFieldFromElement(
                getElement(an.value),
                "stockCode"
              )?.value;

              return (
                <>
                  {code ? (
                    <StockPicker
                      useStockData={useStockData}
                      text={text}
                      code={code}
                    />
                  ) : null}
                </>
              );
            },
            internal_link: (text: string) => {
              const el = getElement(an.value);
              const articleId = el?.relation?.id;
              const newWindow = getFieldFromElement(el, "newWindow");
              const noFollow = getFieldFromElement(el, "noFollow");

              if (!el || !articleId) {
                return null;
              }

              return (
                <Link
                  to={RouteFactory.article(articleId)}
                  data-testid="internal_link"
                  target={newWindow ? "_blank" : "_self"}
                  rel={noFollow ? "nofollow noopener" : "noopener"}
                >
                  {text}
                </Link>
              );
            },
            external_link: (text: string) => {
              const el = getElement(an.value);

              if (!el) {
                return null;
              }

              const href = getFieldFromElement(el, "uri")?.value;
              const newWindow = getFieldFromElement(el, "newWindow");
              const noFollow = getFieldFromElement(el, "noFollow");

              return (
                <a
                  href={href}
                  target={newWindow ? "_blank" : "_self"}
                  rel={
                    noFollow && newWindow ? "nofollow noreferrer" : "noreferrer"
                  }
                  data-testid="external_link"
                >
                  {text}
                </a>
              );
            },
            bold: (text: string) => {
              return <strong data-annotation="bold">{text}</strong>;
            },
            italic: (text: string) => {
              return <i data-annotation="italic">{text}</i>;
            },
            underline: (text: string) => {
              return <u data-annotation="underline">{text}</u>;
            },
            strike: (text: string) => {
              return <s data-annotation="strike">{text}</s>;
            },
            subscript: (text: string) => {
              return <sub data-annotation="subscript">{text}</sub>;
            },
            superscript: (text: string) => {
              return <sup data-annotation="superscript">{text}</sup>;
            },
            capitals: (text: string) => {
              return (
                <span data-annotation="capitals">{text.toUpperCase()}</span>
              );
            },
          };

          // We can't use String.slice directly as need to iterate over characters
          // due to multibyte Unicode characters
          // "💸💸💸".length => 6
          // [..."💸💸💸"].length => 3
          // As an optimisation, only prepare chars if there are annotations
          // This can be optimised further: we can only spread once
          const chars = [...lastSpan.text];
          const replacementText = chars
            .slice(an.index - offset, an.index + an.length - offset)
            .join("");
          const replaced: Span[] = [
            { type: "TEXT", text: chars.slice(0, an.index - offset).join("") },
            {
              type: "NODE",
              node: (
                <React.Fragment key={i}>
                  {
                    // If a replacement element is not found, use a span with a debug element
                    replacements[an.name]?.(replacementText) ?? (
                      <span
                        data-missing-annotation={an.name}
                        data-annotation-id={an.value}
                      >
                        {replacementText}
                      </span>
                    )
                  }
                </React.Fragment>
              ),
            },
            {
              type: "TEXT",
              text: chars.slice(an.index + an.length - offset).join(""),
            },
          ];

          return [...acc.slice(0, -1), ...replaced];
        },
        [{ type: "TEXT", text }]
      );

    return (
      <p data-testid="textBlockWithAnnotations" className={className}>
        {nodes.map((n, i) => {
          if (n.type === "TEXT") {
            return <span key={`span-${i}`}>{n.text}</span>;
          } else {
            return n.node;
          }
        })}
      </p>
    );
  }

  return (
    <p data-testid="textBlockWithoutAnnotations" className={className}>
      {text}
    </p>
  );
}

export function Image({
  src,
  height,
  width,
  caption,
  layout,
}: {
  src: string;
  /** Intrinsic height; specify this to avoid layout shift */
  height?: number;
  /** Intrinsic width; specify this to avoid layout shift */
  width?: number;
  caption?: string;
  layout?: string;
}) {
  return (
    <figure className="relative bg-gray-250">
      <img
        className="mx-auto max-h-[17rem] max-w-full blur-none md:max-h-[29rem] lg:max-h-[33rem]"
        src={src}
        height={height}
        width={width}
      />

      {caption ? (
        <figcaption
          className={cx("w-full p-2 text-2xs", {
            "absolute bottom-0 left-0 bg-black bg-opacity-50 text-white":
              layout == "overlay",
            "text-gray-550": layout != "overlay",
          })}
        >
          {caption}
        </figcaption>
      ) : null}
    </figure>
  );
}

export function RawHtml({ html }: { html: string }) {
  return <div dangerouslySetInnerHTML={{ __html: html }}></div>;
}

export function ExternalLink({ src }: { src: Array<string | undefined> }) {
  return (
    <div
      style={{
        border: "solid 3px var(--c-accent)",
        padding: "var(--m-m)",
        color: "var(--c-accent)",
        margin: "var(--m-m) 0",
      }}
      className="break-words"
    >
      <i>More on this</i>

      {src.filter(Boolean).map((s, i) => (
        <div key={i}>
          <Link to={s || ""} target="_blank" rel="noopener noreferrer">
            {s}
          </Link>
        </div>
      ))}
    </div>
  );
}

export function Note({ value }: { value: string }) {
  return (
    <div
      style={{
        background: "var(--c-accent)",
        color: "white",
        padding: "var(--m-m)",
        fontFamily: "monospace",
      }}
    >
      <div style={{ fontSize: "50%" }}>NOTE</div>
      <p style={{ margin: 0 }}>{value}</p>
    </div>
  );
}

export interface StockPickerProps {
  text: string;
  code: string;
  useStockData: (code: string) => StockData | undefined;
}

export function StockPicker({ text, code, useStockData }: StockPickerProps) {
  // TODO NGDP-32: Styling, functionality are only placeholders

  const [openWidget, setOpenWidget] = useState(false);
  const handleOpenWidget = () => {
    setOpenWidget(true);
  };
  const handleCloseWidget = () => {
    setOpenWidget(false);
  };
  const stockData = useStockData(code);
  const displayWidget = openWidget && stockData && code;
  const {
    percentageChange,
    percentageChangeColor,
    percentageChangeBGColor,
    percentageChangeSign,
  } = getPercentageChangeInfo(stockData?.CHANGES, stockData?.PREVIOUS);

  const gotChanges = stockData?.CHANGES !== 0;

  return (
    <>
      <span
        className={cx("cursor-pointer text-lg font-semibold underline", {
          [percentageChangeColor]: percentageChangeColor && gotChanges,
          [percentageChangeBGColor]: percentageChangeBGColor && gotChanges,
          "hover:text-white": gotChanges,
        })}
        data-testid={`stock-${code}`}
        onClick={handleOpenWidget}
      >
        {text} : {code} {percentageChangeSign}
        {percentageChange ?? 0}%
      </span>
      {displayWidget && (
        <StockPickerWidget
          code={code}
          handleClose={handleCloseWidget}
          stockData={stockData}
        />
      )}
    </>
  );
}

export interface BlockProps {
  rootClassName?: string;
  children?: React.ReactElement;
}

export function Block({
  rootClassName,
  children,
}: BlockProps): React.ReactElement {
  return <div className={cx("block-component", rootClassName)}>{children}</div>;
}

export interface BlockTitleProps {
  rootClassName?: string;
  rootLinkClassName?: string;
  link?: string;
  text: string;
  children?: React.ReactNode;
}
export function BlockTitle({
  rootClassName = "",
  rootLinkClassName = "",
  link = "",
  text,
  children,
}: BlockTitleProps): React.ReactElement {
  return (
    <h2
      className={cx(
        rootClassName,
        "font-poppins text-lg font-bold text-gray-850",
        "lg:text-3xl"
      )}
    >
      {link ? (
        <Link
          to={link}
          className={cx(rootLinkClassName, "hover:underline")}
          target="_blank"
          rel="noopener noreferrer"
        >
          {text}
          <img
            src={
              rootLinkClassName.includes("text-white")
                ? iconForwardArrowWhite
                : iconForwardArrowBlack
            }
            alt={text}
            width="8"
            height="13"
            className="mb-1 ml-2 inline"
          />
          {children}
        </Link>
      ) : (
        <>
          {text}
          {children}
        </>
      )}
    </h2>
  );
}
